import { UserGroupMemberRole, type UserUpdateApiData } from '@navio/nsdk';
import { membershipRolePatch } from '../UserGroups/membershipRole';
import {
  ACCOUNT_FIELDS,
  type MemberFormValues,
  MEMBERSHIP_ROLE_FIELDS,
  NOTIFICATION_FIELDS,
  PRIMARY_CONTACT_FIELDS,
  ROLE_FIELDS
} from './memberFormSchema';

/** Which halves of the sheet this viewer may save. See `OrganizationMemberSheet`'s props. */
export type MemberSheetPermissions = {
  canEditAccount: boolean;
  canEditNotifications: boolean;
  canEditAdmin: boolean;
  canManagePrimaryContact: boolean;
  /** Leasing company or car dealership: the sheet can mark a member as an employee. */
  canAssignMembershipRole: boolean;
};

type MembershipPatch = Partial<
  Pick<MemberFormValues, 'smsBookingNotifications' | 'smsRecallProposals' | 'isAdmin' | 'isPrimaryContact'>
> & { role?: UserGroupMemberRole };

type Args = {
  values: MemberFormValues;
  initialValues: MemberFormValues;
  permissions: MemberSheetPermissions;
};

const MEMBER_FORM_FIELDS = [
  ...ACCOUNT_FIELDS,
  ...NOTIFICATION_FIELDS,
  ...ROLE_FIELDS,
  ...MEMBERSHIP_ROLE_FIELDS,
  ...PRIMARY_CONTACT_FIELDS
];

/**
 * TanStack refreshes the form from `initialValues` only until the first edit, so a field
 * the viewer never changed can still hold what the sheet opened with after the member row
 * refetched (the cars review can turn SMS on). Unedited fields take the row's value, so
 * saving an unrelated edit never writes the stale one back.
 */
export const keepEditedValues = ({
  values,
  initialValues,
  isEdited
}: Pick<Args, 'values' | 'initialValues'> & {
  isEdited: (field: keyof MemberFormValues) => boolean;
}): MemberFormValues =>
  MEMBER_FORM_FIELDS.reduce<MemberFormValues>(
    (merged, field) => (isEdited(field) ? { ...merged, [field]: values[field] } : merged),
    initialValues
  );

const hasChanged = (
  { values, initialValues }: Pick<Args, 'values' | 'initialValues'>,
  fields: readonly (keyof MemberFormValues)[]
) => fields.some((field) => values[field] !== initialValues[field]);

/**
 * The account half, or undefined when untouched or locked. Sending a field the
 * viewer may not change earns a 403 for the whole request, so a locked field is
 * never sent — not even unchanged.
 */
export const buildAccountPatch = ({
  values,
  initialValues,
  permissions
}: Args): UserUpdateApiData['body'] | undefined => {
  if (!permissions.canEditAccount || !hasChanged({ values, initialValues }, ACCOUNT_FIELDS)) {
    return undefined;
  }
  return {
    firstName: values.firstName,
    lastName: values.lastName,
    phoneNumber: values.phoneNumber,
    email: values.email || null
  };
};

/**
 * The membership half — one endpoint for notifications, role and primary contact,
 * each behind its own permission. Empty when nothing the viewer owns changed.
 */
export const buildMembershipPatch = ({ values, initialValues, permissions }: Args): MembershipPatch => ({
  ...(permissions.canEditNotifications && hasChanged({ values, initialValues }, NOTIFICATION_FIELDS)
    ? { smsBookingNotifications: values.smsBookingNotifications, smsRecallProposals: values.smsRecallProposals }
    : {}),
  ...(permissions.canEditAdmin && hasChanged({ values, initialValues }, ROLE_FIELDS)
    ? { isAdmin: values.isAdmin }
    : {}),
  ...membershipRolePatch({
    canAssignMembershipRole: permissions.canAssignMembershipRole,
    next: values,
    previous: initialValues
  }),
  ...(permissions.canManagePrimaryContact && hasChanged({ values, initialValues }, PRIMARY_CONTACT_FIELDS)
    ? { isPrimaryContact: values.isPrimaryContact }
    : {})
});
